import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Home, Scale, Dog, ClipboardCheck, Calculator, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { Kicker } from "@/components/form";

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

// Softfair-Rechner-Links (öffnen in neuem Tab)
const CARDS = [
  {
    key: "privathaftpflicht",
    icon: ShieldCheck,
    title: "Privathaftpflichtversicherung",
    desc: "Schützt Sie vor den finanziellen Folgen, wenn Sie versehentlich Dritten einen Personen- oder Sachschaden zufügen – privat unverzichtbar.",
    calcUrl: "https://lotse.softfair-server.de/b2c/ergebnis/phv?userexid=20accb08-2915-11e8-8000-acbd46a275f6",
  },
  {
    key: "hausrat",
    icon: Home,
    title: "Hausratversicherung",
    desc: "Absicherung Ihres gesamten Hausrats gegen Feuer, Einbruchdiebstahl, Leitungswasser und Sturm – zuverlässig zum Neuwert.",
    calcUrl: "https://b2c.softfair-server.de/hausrat?externeUserId=20accb08-2915-11e8-8000-acbd46a275f6",
  },
  {
    key: "rechtsschutz",
    icon: Scale,
    title: "Rechtsschutzversicherung",
    desc: "Übernimmt Anwalts-, Gerichts- und Gutachterkosten, damit Sie Ihr gutes Recht ohne finanzielles Risiko durchsetzen können.",
    calcUrl: "https://lotse.softfair-server.de/b2c/ergebnis/rs?userexid=20accb08-2915-11e8-8000-acbd46a275f6",
  },
  {
    key: "hundehalter",
    icon: Dog,
    title: "Hundehalterhaftpflichtversicherung",
    desc: "Deckt Schäden ab, die Ihr Hund verursacht – in vielen Bundesländern gesetzlich vorgeschrieben und dringend zu empfehlen.",
    calcUrl: "https://lotse.softfair-server.de/b2c/ergebnis/hundehalter?userexid=20accb08-2915-11e8-8000-acbd46a275f6",
  },
  {
    key: "versicherungs-check",
    icon: ClipboardCheck,
    title: "Versicherungs-Check (Bedarfsermittlung)",
    desc: "Kostenlose Analyse Ihrer bestehenden Verträge – wir decken Lücken, Doppelversicherungen und Sparpotenziale auf.",
    calcUrl: "https://lotse.softfair-server.de/b2c/bedarfsermittlung?userexid=20accb08-2915-11e8-8000-acbd46a275f6",
  },
];

export default function Versicherungsvergleich() {
  const handleCalc = (card) => {
    if (card.calcUrl) {
      window.open(card.calcUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.info("Der Online-Rechner wird in Kürze freigeschaltet. Gern beraten wir Sie schon jetzt persönlich.");
    }
  };

  return (
    <div data-testid="versicherungsvergleich-page">
      <Seo
        title="Versicherungsvergleich"
        description="Vergleichen Sie Privathaftpflicht, Hausrat, Rechtsschutz und Hundehalterhaftpflicht – oder nutzen Sie unseren kostenlosen Versicherungs-Check zur Bedarfsermittlung."
        path="/versicherungsvergleich"
        keywords={["Versicherungsvergleich", "Privathaftpflicht", "Hausratversicherung", "Rechtsschutzversicherung", "Hundehalterhaftpflicht", "Versicherungs-Check"]}
      />

      {/* Hero */}
      <section className="relative pt-40 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fade} className="max-w-3xl">
            <Kicker>Versicherungsvergleich</Kicker>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-none">
              Der passende Schutz – transparent verglichen
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed max-w-2xl">
              Berechnen Sie Ihren Beitrag in wenigen Schritten oder lassen Sie sich kostenlos und unabhängig beraten. Wir vergleichen für Sie die Tarife führender Gesellschaften.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cards */}
      <section className="bg-navy-900 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CARDS.map((card, i) => (
              <motion.div
                key={card.key}
                {...fade}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`vv-card-${card.key}`}
                className="flex flex-col bg-navy-800 border border-white/5 rounded-md p-8 hover:border-gold-500/40 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="w-12 h-12 rounded-sm bg-gold-500/15 flex items-center justify-center mb-6">
                  <card.icon size={22} className="text-gold-500" />
                </span>
                <h3 className="text-xl font-bold text-white leading-snug">{card.title}</h3>
                <p className="mt-4 text-slate-400 leading-relaxed flex-1">{card.desc}</p>

                <div className="mt-8 flex flex-col gap-3">
                  <button
                    data-testid={`vv-calc-${card.key}`}
                    onClick={() => handleCalc(card)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-navy-900 font-bold rounded-sm px-6 py-3 text-sm hover:bg-gold-300 transition-colors"
                  >
                    <Calculator size={16} /> Jetzt berechnen
                  </button>
                  <Link
                    to="/kontakt"
                    data-testid={`vv-consult-${card.key}`}
                    className="w-full inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold rounded-sm px-6 py-3 text-sm hover:border-gold-500 hover:text-gold-500 transition-colors"
                  >
                    <MessageCircle size={16} /> Kostenlose Beratung
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
